import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";

import {
  Drawer,
  Text,
  Flex,
  Button,
  Devider,
  Spinner,
  PaginationLoadMore,
  PaginationContext,
  ModalContext,
  ViewerContext,
} from "dash-ui";

import { useGetAlertConfigsQuery } from "gql/generated.graphql";

const TextContainer = styled(Flex)`
  overflow: hidden;
  flex: 2;
  @media ${({ theme }) => theme.devices.mobile} {
    flex: 1;
  }
`;

const AddAlertModal = () => {
  const { t } = useTranslation(["common", "pages"]);
  const { state: paginationState, setTotal } = useContext(PaginationContext);
  const theme = useTheme();
  const { isOpenCurrent, toggleModal, setPayload } = useContext(ModalContext);
  const { broker } = useContext(ViewerContext);
  const isOpen = isOpenCurrent["AlertListModal"];

  const variables = useMemo(
    () => ({
      pagination: {
        limit: paginationState.perPage,
      },
    }),
    [paginationState]
  );

  const { data, fetchMore, loading } = useGetAlertConfigsQuery({
    variables,
    fetchPolicy: "network-only",
    skip: !broker,
  });

  const { alerts = [], totalCount } = useMemo(
    () => ({
      alerts: data?.getAlertConfigs.collection || [],
      totalCount: data?.getAlertConfigs.totalCount || 0,
    }),
    [data]
  );

  const onRemoveButtonClick = useCallback(
    (alertItem: { _id: string }) => (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      toggleModal(true, "RemoveAlertModal");
      setPayload({ alertConfigId: alertItem._id, paginationState });
      toggleModal(false, "AlertListModal");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paginationState]
  );

  const onViewButtonClick = useCallback(
    (alertItem: { _id: string }) => (e: { stopPropagation: () => void }) => {
      e.stopPropagation();
      toggleModal(true, "AddAlertModal");
      setPayload({ alertConfigId: alertItem._id, paginationState });
      toggleModal(false, "AlertListModal");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paginationState]
  );

  useEffect(() => {
    setTotal(totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  return (
    <Drawer
      isOpen={isOpen}
      onRequestClose={() => toggleModal(false, "AlertListModal")}
      title={t("pages:alertPage.labels:manageAlertsModalTitle") || ""}
      closeIcon
    >
      <Flex flexDirection="column">
        <Flex>
          <Text color={theme.colors.n80}>
            {t("pages:alertPage.labels:manageAlertsModalSuTitle")}
          </Text>
        </Flex>
        <Flex w="100%" flexDirection="column">
          {alerts.map((alert) => (
            <Flex key={alert._id} mt={3} flexDirection="column" w="100%">
              <Flex
                flex={1}
                w="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <TextContainer>
                  <Text ellipsis>{alert.name}</Text>
                </TextContainer>
                <Flex minWidth={128} flex={1} justifyContent="flex-end">
                  <Button
                    h={32}
                    px={2}
                    mr={2}
                    icon="visibility"
                    variation="flat"
                    onClick={onViewButtonClick(alert)}
                  >
                    {t("common:labels:view")}
                  </Button>

                  <Button
                    h={32}
                    px={2}
                    icon="trash"
                    colorVariation="danger"
                    variation="flat"
                    onClick={onRemoveButtonClick(alert)}
                  />
                </Flex>
              </Flex>
              <Devider w="100%" mt={3} />
            </Flex>
          ))}
        </Flex>
        <PaginationLoadMore
          w="100%"
          medium
          fetchMoreProps={{
            fetchMore,
            variables,
            queryName: "getAlertConfigs",
          }}
        >
          {t("common:labels.seeMore")}
        </PaginationLoadMore>
        {loading && (
          <Flex w="100%" alignItems="center" justifyContent="center">
            <Spinner />
          </Flex>
        )}
      </Flex>
    </Drawer>
  );
};

export default AddAlertModal;
